import type { ResolutionString } from 'charting_library';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { CURRENT_APP_VERSION, clearDataMigration } from '@/utils/zustand_helpers.ts';
import {
  type ChartOverlayKey,
  type ChartOverlayVisibility,
  DEFAULT_CHART_OVERLAY_VISIBILITY,
  normalizeOverlayVisibility,
} from './chartOverlayConfig';

export type TradingViewSavedChart = Record<string, unknown>;

export type TerminalChartView = 'candles' | 'depth';

export interface CandlestickChartState {
  resolution: ResolutionString;
  autosize: boolean;
  savedChartStates: Record<string, TradingViewSavedChart>;
  overlayVisibility: ChartOverlayVisibility;
  chartView: TerminalChartView;
  /** Toggle injected into the TradingView header. False means consumers must offer their own. */
  chartViewToggleReady: boolean;

  // Actions
  setResolution: (resolution: ResolutionString) => void;
  setAutosize: (autosize: boolean) => void;
  setOverlayVisibility: (key: ChartOverlayKey, visible: boolean) => void;
  setChartView: (chartView: TerminalChartView) => void;
  setChartViewToggleReady: (ready: boolean) => void;
  saveChartStateForSymbol: (symbol: string, state: TradingViewSavedChart) => void;
  getSavedChartStateForSymbol: (symbol: string) => TradingViewSavedChart | undefined;
  reset: () => void;
}

const DEFAULT_STATE: Pick<
  CandlestickChartState,
  'resolution' | 'autosize' | 'savedChartStates' | 'overlayVisibility' | 'chartView' | 'chartViewToggleReady'
> = {
  resolution: '1' as ResolutionString,
  autosize: true,
  savedChartStates: {},
  overlayVisibility: DEFAULT_CHART_OVERLAY_VISIBILITY,
  chartView: 'candles',
  chartViewToggleReady: false,
};

export const useCandlestickChartStore = create<CandlestickChartState>()(
  persist<CandlestickChartState, [], [], Partial<CandlestickChartState>>(
    (set, get) => ({
      ...DEFAULT_STATE,

      setResolution: (resolution: ResolutionString) => {
        set({ resolution });
      },

      setAutosize: (autosize: boolean) => set({ autosize }),

      setOverlayVisibility: (key, visible) =>
        set((state) => ({
          overlayVisibility: { ...state.overlayVisibility, [key]: visible },
        })),

      setChartView: (chartView) => set({ chartView }),

      setChartViewToggleReady: (chartViewToggleReady) => set({ chartViewToggleReady }),

      saveChartStateForSymbol: (symbol: string, state: TradingViewSavedChart) =>
        set((currentState) => ({
          savedChartStates: {
            ...currentState.savedChartStates,
            [symbol]: state,
          },
        })),

      getSavedChartStateForSymbol: (symbol: string) => get().savedChartStates[symbol],

      reset: () =>
        set((state) => ({
          ...DEFAULT_STATE,
          savedChartStates: state.savedChartStates,
          // Describes the live widget, not user data.
          chartViewToggleReady: state.chartViewToggleReady,
        })),
    }),
    {
      name: 'dex-charts-v3',
      version: CURRENT_APP_VERSION,
      migrate: clearDataMigration,
      storage: createJSONStorage(() => localStorage),
      merge: (persisted, current) => {
        const persistedState = (persisted ?? {}) as Partial<CandlestickChartState>;
        return {
          ...current,
          ...persistedState,
          // Backfill overlay keys the persisted state predates (or the whole object, pre-feature).
          overlayVisibility: normalizeOverlayVisibility(persistedState.overlayVisibility),
          chartView: persistedState.chartView === 'depth' ? 'depth' : 'candles',
        };
      },
      partialize: (state) => ({
        resolution: state.resolution,
        autosize: state.autosize,
        savedChartStates: state.savedChartStates,
        overlayVisibility: state.overlayVisibility,
        chartView: state.chartView,
      }),
    }
  )
);
